import React, { useEffect } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { auth0Config, isAuth0Configured } from '../config/auth0';
import { setTokenGetter } from '../services/api';
import { isLocalLoopbackHostname } from '../utils/companyTenantResolver';

// Inner component to access Auth0 hooks and set token getter
const Auth0TokenSetter = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated, isLoading, logout, loginWithRedirect } = useAuth0();

  useEffect(() => {
    // Only clear tokenGetter if we're sure the user is not authenticated (not just loading)
    // Don't clear during initial load - wait for Auth0 to finish checking authentication
    if (!isLoading && !isAuthenticated) {
      console.warn('⚠️ Auth0TokenSetter - User not authenticated after loading, clearing tokenGetter');
      setTokenGetter(null);
      return;
    }

    // Set token getter function when authenticated
    if (isAuthenticated) {
      console.log('✅ Auth0TokenSetter - User authenticated, setting tokenGetter');
      // Set token getter function for RTK Query
      setTokenGetter(async () => {
        try {
          console.log('🔍 Auth0TokenSetter - Getting access token...');
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: auth0Config.audience,
              scope: 'openid profile email offline_access'
            }
          });
          console.log('✅ Auth0TokenSetter - Token retrieved successfully:', token ? 'Token exists' : 'No token');
          return token;
        } catch (error) {
          console.error('❌ Auth0TokenSetter - Error getting access token:', error);
          console.error('❌ Auth0TokenSetter - Error details:', error.message, error.error);
          
          // Check if it's a refresh token error
          if (error.message && (error.message.includes('Missing Refresh Token') || error.message.includes('refresh_token'))) {
            console.warn('⚠️ Auth0TokenSetter - Refresh token expired. Forcing re-authentication...');
            // Force logout and redirect to login
            try {
              logout({
                logoutParams: {
                  returnTo: window.location.origin
                }
              });
            } catch (logoutError) {
              console.error('❌ Auth0TokenSetter - Error during logout:', logoutError);
              // Fallback: redirect to login
              loginWithRedirect();
            }
          }
          
          return null;
        }
      });
    } else if (isLoading) {
      console.log('🔍 Auth0TokenSetter - Auth0 is still loading, waiting...');
      // Don't clear tokenGetter during loading - user might still be authenticated
    }
  }, [getAccessTokenSilently, isAuthenticated, isLoading, logout, loginWithRedirect]);

  return children;
};

const Auth0Wrapper = ({ children }) => {
  // Debug: Log Auth0 configuration
  console.log('🔍 Auth0 Configuration:', {
    domain: auth0Config.domain,
    clientId: auth0Config.clientId,
    redirectUri: auth0Config.redirectUri,
    envClientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
    envDomain: process.env.REACT_APP_AUTH0_DOMAIN
  });
  
  // Additional debug info
  console.log('🔍 Environment Variables:', {
    'REACT_APP_AUTH0_DOMAIN': process.env.REACT_APP_AUTH0_DOMAIN,
    'REACT_APP_AUTH0_CLIENT_ID': process.env.REACT_APP_AUTH0_CLIENT_ID,
    'REACT_APP_AUTH0_AUDIENCE': process.env.REACT_APP_AUTH0_AUDIENCE,
    'REACT_APP_AUTH0_REDIRECT_URI': process.env.REACT_APP_AUTH0_REDIRECT_URI
  });

  // Check if Auth0 is properly configured
  if (!isAuth0Configured()) {
    console.warn('Auth0 is not properly configured. Please check your environment variables.');
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <h4>Auth0 Configuration Required</h4>
          <p className="text-muted">
            Please configure Auth0 environment variables in your .env file
          </p>
          <div className="mt-3">
            <code>
              REACT_APP_AUTH0_DOMAIN=your-domain.auth0.com<br/>
              REACT_APP_AUTH0_CLIENT_ID=your-client-id<br/>
              REACT_APP_AUTH0_AUDIENCE=your-audience<br/>
              REACT_APP_AUTH0_REDIRECT_URI=http://localhost:3002
            </code>
          </div>
        </div>
      </div>
    );
  }

  const onRedirectCallback = (appState) => {
    // Simple: after code exchange, go to the requested route or home
    window.location.replace(appState?.returnTo || '/');
  };

  // Get redirect URI dynamically at runtime (not build time)
  const getRedirectUri = () => {
    let redirectUri;
    if (process.env.REACT_APP_AUTH0_REDIRECT_URI) {
      redirectUri = process.env.REACT_APP_AUTH0_REDIRECT_URI;
    } else if (typeof window !== 'undefined' && window.location) {
      redirectUri = window.location.origin;
    } else {
      redirectUri = 'http://localhost:3002';
    }
    // Remove trailing slash - Auth0 is strict about exact URL matches
    redirectUri = redirectUri.replace(/\/$/, '');
    
    console.log('🔍 Auth0Wrapper - getRedirectUri:', redirectUri);
    console.log('🔍 Auth0Wrapper - window.location.origin:', window.location?.origin);
    
    return redirectUri;
  };

  // Get client ID dynamically at runtime (not build time)
  // ALWAYS check hostname first - ignore process.env which is set at build time
  const getClientId = () => {
    // Auto-detect environment based on hostname at runtime (this is the source of truth)
    if (typeof window !== 'undefined' && window.location) {
      const hostname = window.location.hostname;
      console.log('🔍 Auth0Wrapper - Current hostname:', hostname);
      
      // Staging environment - ALWAYS use staging client ID
      if (hostname === 'staging.krossglobalmarketing.com' || hostname.includes('staging')) {
        console.log('🔍 Auth0Wrapper - Using STAGING client ID: P4NCRrCFA6h7UZtnHNEnBHbyGzM7yJXx');
        return 'P4NCRrCFA6h7UZtnHNEnBHbyGzM7yJXx'; // Staging client ID
      }
      
      // Production environment
      if (hostname === 'krossglobalmarketing.com' || hostname === 'www.krossglobalmarketing.com') {
        console.log('🔍 Auth0Wrapper - Using PRODUCTION client ID: Ymq5KisXUcYdVTxnZKewnISyNO2JU81q');
        return 'Ymq5KisXUcYdVTxnZKewnISyNO2JU81q'; // Production client ID
      }
      
      // Local development - default to staging for testing (IPv4 + IPv6 loopback)
      if (isLocalLoopbackHostname(hostname)) {
        console.log('🔍 Auth0Wrapper - Using STAGING client ID (localhost): P4NCRrCFA6h7UZtnHNEnBHbyGzM7yJXx');
        return 'P4NCRrCFA6h7UZtnHNEnBHbyGzM7yJXx'; // Staging client ID for local dev
      }
    }
    
    // Default fallback to staging (safest for testing)
    console.log('🔍 Auth0Wrapper - Using STAGING client ID (fallback): P4NCRrCFA6h7UZtnHNEnBHbyGzM7yJXx');
    return 'P4NCRrCFA6h7UZtnHNEnBHbyGzM7yJXx';
  };

  // Build authorizationParams dynamically - must be a function or computed each time
  const getAuthorizationParams = () => {
    const params = {
      redirect_uri: getRedirectUri(), // Always get fresh value
      scope: auth0Config.scope
    };
    
    // Only add audience if it's defined
    if (auth0Config.audience) {
      params.audience = auth0Config.audience;
    }
    
    return params;
  };

  // Get client ID at runtime
  const clientId = getClientId();
  console.log('🔍 Auth0Wrapper - Final clientId:', clientId);
  console.log('🔍 Auth0Wrapper - window.location.hostname:', typeof window !== 'undefined' ? window.location.hostname : 'N/A');

  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={clientId}
      authorizationParams={getAuthorizationParams()}
      useRefreshTokens={auth0Config.useRefreshTokens}
      cacheLocation={auth0Config.cacheLocation}
      advancedOptions={auth0Config.advancedOptions}
      onRedirectCallback={onRedirectCallback}
    >
      <Auth0TokenSetter>
        {children}
      </Auth0TokenSetter>
    </Auth0Provider>
  );
};

export default Auth0Wrapper;
